// Free-text chip input: type a value, press Enter or click Add, and it
// becomes a removable chip. For short closed sets the user defines
// themselves (a marker's category names); `MultiSelect` remains the control
// for picking from server-owned options. Shares the chip styling of
// `MultiSelect` so both read as one family.
//
// The chip area reserves a fixed minimum height (`.marker-chip-input__chips`)
// so adding the first chips never shifts the controls beneath it.
import { useId, useState, type KeyboardEvent, type ReactNode } from "react";

import { Button } from "./Button";
import { Field } from "./TextInput";

export interface ChipInputProps {
  /** Accessible label of the Add button, e.g. "Add category". */
  addLabel?: string;
  disabled?: boolean;
  help?: ReactNode;
  id?: string;
  label: string;
  onChange: (values: string[]) => void;
  placeholder?: string;
  required?: boolean;
  values: readonly string[];
}

export function ChipInput({
  addLabel = "Add",
  disabled = false,
  help,
  id,
  label,
  onChange,
  placeholder,
  required,
  values,
}: ChipInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [draft, setDraft] = useState("");

  function add(): void {
    const value = draft.trim();
    if (value.length === 0) {
      return;
    }
    setDraft("");
    if (values.includes(value)) {
      return;
    }
    onChange([...values, value]);
  }

  function remove(value: string): void {
    onChange(values.filter((item) => item !== value));
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      // Enter adds a chip; it never submits the surrounding form.
      event.preventDefault();
      add();
    } else if (
      event.key === "Backspace" &&
      draft.length === 0 &&
      values.length > 0
    ) {
      remove(values[values.length - 1] as string);
    }
  }

  return (
    <Field help={help} htmlFor={inputId} label={label} required={required}>
      <div className="marker-chip-input">
        <div className="marker-chip-input__row">
          <input
            className="marker-field__input"
            disabled={disabled}
            id={inputId}
            placeholder={placeholder}
            type="text"
            value={draft}
            onChange={(event) => {
              setDraft(event.currentTarget.value);
            }}
            onKeyDown={onKeyDown}
          />
          <Button
            aria-label={addLabel}
            disabled={disabled || draft.trim().length === 0}
            type="button"
            variant="secondary"
            onClick={add}
          >
            Add
          </Button>
        </div>
        <ul aria-label={`${label} list`} className="marker-chip-input__chips">
          {values.map((value) => (
            <li className="marker-chip-input__chip" key={value}>
              <span className="marker-chip-input__chip-label">{value}</span>
              <button
                aria-label={`Remove ${value}`}
                className="marker-chip-input__chip-remove"
                disabled={disabled}
                type="button"
                onClick={() => {
                  remove(value);
                }}
              >
                <RemoveIcon />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Field>
  );
}

function RemoveIcon() {
  return (
    <svg
      aria-hidden="true"
      className="marker-chip-input__icon"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
